import {
    OreButton,
    OrePill,
    OreStack,
    OreText,
    OreTimeline,
    SvgArrowWidget,
    SvgLogo2,
    SvgMinus,
    SvgPlus,
    SvgUserFilled
} from '@edenredespana/oreneta'
import { Trans, useTranslation } from 'react-i18next'
import { useHelpCenter } from '@/HelpCenter/ui/context'
import { HelpCenterTranslations } from '@/HelpCenter/ui/helpCenterTranslations'
import { useNavigate, useParams } from 'react-router-dom'
import { navigationRoutes } from "@/Shared/ui/routers"
import { useEffect, useState } from 'react'
import { TicketDetailFooter } from '@/HelpCenter/ui/molecules'
import { TicketTimelineStatus } from '@/HelpCenter/domain/TicketModel'
import { HelpCenterError } from './HelpCenterError'

export const TicketDetailContainer = (): JSX.Element => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { ticketId } = useParams<{ ticketId: string }>()
    const { getTicketDetail, ticketDetail, isError, setIsError } = useHelpCenter()
    const [showDescription, setShowDescription] = useState(false)
    const handleBackClick = () => {
        navigate(navigationRoutes.myTickets)
    }

    const parseTimelineItems = (timelineItems: { status: string; date: string }[]) => {
        return timelineItems.map((item, index) => {
            const formattedDate = new Date(item.date).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })

            const getIcon = () => {
                if (item.status === TicketTimelineStatus.Open || item.status === TicketTimelineStatus.Reopened) {
                    return <SvgUserFilled aria-hidden={true} />
                }
                return <SvgLogo2 aria-hidden={true} />
            }

            const getDescription = () => {
                switch (item.status) {
                    case TicketTimelineStatus.Open:
                        return <OreText size="text-md">
                            <Trans>{t(HelpCenterTranslations.ticketDetail.timelineStatus.open, { caseReason: ticketDetail?.caseReason ?? '' })}</Trans>
                        </OreText>
                    case TicketTimelineStatus.InProgress:
                        return <OreText size="text-md"><Trans>{t(HelpCenterTranslations.ticketDetail.timelineStatus.inProgress)}</Trans></OreText>
                    case TicketTimelineStatus.WaitingForAnswer:
                        return <OreText size="text-md"><Trans>{t(HelpCenterTranslations.ticketDetail.timelineStatus.waitingForAnswer)}</Trans></OreText>
                    case TicketTimelineStatus.Resolved:
                        return <OreText size="text-md"><Trans>{t(HelpCenterTranslations.ticketDetail.timelineStatus.resolved)}</Trans></OreText>
                    case TicketTimelineStatus.Reopened:
                        return <OreText size="text-md"><Trans>{t(HelpCenterTranslations.ticketDetail.timelineStatus.reopened, { caseReason: ticketDetail?.caseReason ?? '' })}</Trans></OreText>
                    default:
                        return ''
                }
            }

            const getTone = (): 'success-600' | 'warning-600' | 'cobalt-600' | 'grey' => {
                switch (item.status) {
                    case TicketTimelineStatus.Resolved:
                        return 'success-600'
                    case TicketTimelineStatus.WaitingForAnswer:
                        return 'warning-600'
                    case TicketTimelineStatus.InProgress:
                        return 'cobalt-600'
                    default:
                        return 'grey'
                }
            }

            return {
                icon: getIcon(),
                title: formattedDate,
                status: index === 0 ? (
                    <OreStack direction="row" space="xsmall" placeItems="center" placeContent="start" style={{ marginBottom: '18px' }}>
                        <OreText size="text-md" bold>{t(HelpCenterTranslations.ticketDetail.timelineStatus.status)}</OreText>
                        <OrePill tone={getTone()}>{item.status}</OrePill>
                    </OreStack>
                ) : undefined,
                description: <OreText size="text-md">{getDescription()}</OreText>
            }
        })
    }


    useEffect(() => {
        if (ticketId) {
            try {
                getTicketDetail(ticketId)
            } catch (error) {
                setIsError(true)
            }
        }
    }, [ticketId])

    if (isError) {
        return <HelpCenterError />
    }

    return (
        <div className="page-wrapper" style={{ paddingTop: '12px' }}>
            <OreStack space="medium" direction='column' placeContent="stretch" placeItems='stretch'>
                <OreStack space="large" direction="row" placeContent="space-between">
                    <OreButton
                        category={'tertiary'}
                        icon={<SvgArrowWidget direction="left" />}
                        onClick={handleBackClick}
                    >
                        {t(HelpCenterTranslations.navigation.back)}
                    </OreButton>
                    <OreText size="text-lg">{t(HelpCenterTranslations.ticketDetail.title, { caseNumber: ticketDetail?.caseNumber })}</OreText>
                </OreStack>
                <OreStack space="medium" placeContent="stretch">
                    <OreStack space="2xsmall" >
                        <OreText size="text-md" bold>{ticketDetail?.nameContact}</OreText>
                        <OreText size="text-md">{ticketDetail?.caseReason}</OreText>
                    </OreStack>
                    <OreStack space="medium" placeItems='start' >
                        <OreText size="text-md">{ticketDetail?.subject}</OreText>
                        {showDescription && (
                            <>
                                <OreText size="text-md" bold>{t(HelpCenterTranslations.ticketDetail.description)}</OreText>
                                <OreText size="text-md" style={{ whiteSpace: 'pre-line' }}>{ticketDetail?.description}</OreText>
                            </>
                        )}
                        {ticketDetail?.description && (
                            <OreButton
                                category="highlight"
                                iconAlign='right'
                                icon={showDescription ? <SvgMinus /> : <SvgPlus />}
                                onClick={() => setShowDescription(prev => !prev)}
                                style={{ outline: 'none', boxShadow: 'none' }}
                            >
                                {showDescription
                                    ? t(HelpCenterTranslations.ticketDetail.seeLess)
                                    : t(HelpCenterTranslations.ticketDetail.seeMore)}
                            </OreButton>
                        )}
                    </OreStack>
                    <OreStack space="medium" placeItems='stretch' placeContent="stretch" style={{ marginBottom: '32px' }}>
                        <OreTimeline items={ticketDetail?.caseHistoricStatusList ? parseTimelineItems(ticketDetail.caseHistoricStatusList) : []} />
                    </OreStack>
                </OreStack>
                <OreStack space="medium" placeContent='center'>
                    <TicketDetailFooter />
                </OreStack>
            </OreStack>
        </div>
    )
}
